import { GenAiPanelProps } from '@verbit-ai/ai-service-web'
import { HeaderProps } from '@verbit-ai/shared-header'

import { Order, OrderStatus, ServiceType } from 'src/models/Order'
import { CrdtSyncResponse, CustomerResponse } from 'src/network/types'
import { TranscriptOld } from 'src/models/TranscriptOld'
import { SessionDeposition, TranscriptMetadata } from 'src/models/TranscriptMetadata'
import { TranscriptionDocumentMetadata } from 'src/models/TranscriptionDocumentMetadata'
import { StreamMessage } from 'src/network/StreamClient'
import { getMockFileUrl, isMockActive, isMockDiarizationEnabled } from 'src/utils/env'
import { DEFAULT_LANGUAGE_CODE } from 'src/models/Language'
import { extname } from 'src/utils/extname'
import { FeatureName } from 'src/models/FeatureFlag'

import { LiveEventPlayer } from './LiveEventPlayer'

type MockSyncResponse = {
    delta: number[]
    serverStateVector: number[]
    lastTimestamp: number
}

type MockData = {
    transcriptJobId: string
    transcriptMetadata: TranscriptMetadata
    transcriptionDocumentMetadata: TranscriptionDocumentMetadata
    initialTranscript: TranscriptOld
    asrHistory: StreamMessage[]
    liveEvents: StreamMessage[]
    insightsApiResults: Exclude<NonNullable<GenAiPanelProps['useMocks']>, boolean>
    enabledFeatures: FeatureName[]
    sharedHeaderConfig: Pick<HeaderProps, 'userConfig' | 'customer'>
    customerData: CustomerResponse
    orderData: Order
    syncResponse: MockSyncResponse
}

type Mode = 'record' | 'replay'

export class MockManager {
    private mode: Mode = 'record'
    private data: MockData
    private liveEventPlayer = new LiveEventPlayer()
    private _isAnonymousUser = false

    private readyPromise: Promise<void>

    constructor(mode: Mode, file: string, isAnonymousUser = false) {
        this.data = this.getinitialMockData()
        this.mode = mode
        this._isAnonymousUser = isAnonymousUser

        if (mode === 'replay') {
            this.readyPromise = this.loadReplayFile(file)
        } else {
            this.readyPromise = Promise.resolve()
        }
    }

    isReady() {
        return this.readyPromise
    }

    getMode() {
        return this.mode
    }

    static async fetchFile(file: string) {
        try {
            return (await (await fetch(file)).json()) as MockData
        } catch (e) {
            throw new Error(`[MOCK] Could not load replay file from "${file}".`)
        }
    }

    private async loadReplayFile(file: string) {
        const data = await MockManager.fetchFile(file)

        if (data) {
            Object.assign(this.data, data)

            // make sure that "Last Update" is synchronized across all insights,
            // and "Updated version available" tooltip isn't shown
            if (this.data.insightsApiResults.insights) {
                Object.keys(this.data.insightsApiResults.insights).forEach((insight) => {
                    this.data.insightsApiResults.insights[insight as keyof MockData['insightsApiResults']['insights']]!.updated_at =
                        new Date().toISOString()
                })
            }

            this.liveEventPlayer.addEvents(data.liveEvents)
        }
    }

    private getinitialMockData(): MockData {
        return {
            transcriptJobId: '',
            transcriptMetadata: {
                ablyClientOptions: {},
                ablyTokenUrl: '',
                scheduledEventId: 9999999,
                sessionId: 999999,
                downloadable: true,
                sessionStartAt: '2024-09-19T07:15:00.000Z',
                sessionStartedAt: '2024-09-19T07:15:00.000Z',
                sessionEndAt: '2024-09-19T08:15:00.000Z',
                sessionFinishedAt: '2024-09-19T09:24:58.979Z',
                transcriptionJobId: 999999,
                customerId: 999999,
                customerName: 'John Doe',
                customerType: null,
                sessionState: {
                    startTime: new Date('2024-09-19T07:15:00.000Z'),
                    deposition: SessionDeposition.LIVE,
                    recording: {
                        startedAt: '2024-09-19T07:15:00.000Z',
                        status: 'live',
                        stopReason: null,
                    },
                },
                transcriptType: 'asr_only',
                sessionName: 'sessionName',
                suppressSpeakerName: false,
                generativeAiEnabled: true,
                vertical: 'education',
                originalLanguageCode: DEFAULT_LANGUAGE_CODE,
                translations: [],
                orderId: 'a98bacec-a09c-45cd-8647-86d7478003e0',
                order: {
                    id: 'a98bacec-a09c-45cd-8647-86d7478003e0',
                    name: 'orderName',
                    status: {
                        name: 'completed',
                        message: null,
                    },
                },
                inputType: 'onsite',
                viewMode: 'document',
                fileUrl: '/demo/deposition-demo.pdf',
            },
            transcriptionDocumentMetadata: {
                customerId: 999999,
                customerName: 'John Doe',
                transcriptState: 'completed',
                documentTitle: 'Mock Document',
                uploadedAt: new Date('2024-09-19T07:15:00.000Z'),
                fileUrl: '/demo/deposition-demo.pdf',
            },
            initialTranscript: {
                paragraphs: [],
                asrParagraphs: [],
                authors: {},
                isDiarizationSupported: false,
                words: {},
                processedTime: 0,
                asrProcessedTime: 0,
                recordingStartedAt: new Date(),
                unfinishedUtteranceId: null,
            },
            asrHistory: [],
            liveEvents: [],
            insightsApiResults: {
                insights: {},
                job_status: 'running',
                process_status: 'completed',
            },
            enabledFeatures: [],
            sharedHeaderConfig: {
                userConfig: {
                    email: 'guest@example.com',
                    feature_flags: {
                        collections_access: false,
                        enable_multi_tenancy: false,
                    },
                    impersonated_by: null,
                    roles: [],
                },
                customer: {
                    access_to_legal_deposition: false,
                    id: '',
                    name: 'Guest',
                    type: 'education',
                },
            },
            customerData: {
                customer: {
                    id: '',
                    name: 'Guest',
                    type: 'education',
                    access_to_legal_deposition: false,
                },
            },
            orderData: {
                id: 'a98bacec-a09c-45cd-8647-86d7478003e0',
                short_id: 'ORD-001',
                transcription_job_id: '999999',
                name: 'Mock Order',
                client_transaction_id: 'mock-txn-id',
                status: { name: OrderStatus.ReadyToConnect },
                internal_status: {},
                last_updated_at: '2024-09-19T07:15:00.000Z',
                input: {
                    type: 'onsite',
                    language: 'en-US',
                    schedule: {
                        start_at: '2024-09-19T07:15:00.000Z',
                        max_duration: 3600,
                    },
                },
                output: [],
                service_type: ServiceType.Live,
                created_at: '2024-09-18T10:00:00.000Z',
                collections: [],
            },
            syncResponse: {
                delta: [],
                serverStateVector: [],
                lastTimestamp: 0,
            },
        }
    }

    getTranscriptJobId() {
        return this.data.transcriptJobId
    }

    getTranscriptMetadata() {
        return this.data.transcriptMetadata
    }

    saveTranscriptMetadata(metadata: TranscriptMetadata) {
        return (this.data.transcriptMetadata = metadata)
    }

    getTranscriptionDocumentMetadata() {
        return this.data.transcriptionDocumentMetadata
    }

    getTranscriptionDocumentId() {
        return 'mock-document-id'
    }

    getInitialTranscript(): TranscriptOld {
        return { ...this.data.initialTranscript, isDiarizationSupported: isMockDiarizationEnabled() }
    }

    saveInitialTranscript(transcript: TranscriptOld) {
        return (this.data.initialTranscript = transcript)
    }

    getAsrHistory() {
        return this.data.asrHistory
    }

    saveAsrHistory(asrHistory: StreamMessage[]) {
        return (this.data.asrHistory = asrHistory)
    }

    saveLiveEvent(events: StreamMessage | StreamMessage[]) {
        events = Array.isArray(events) ? events : [events]
        this.data.liveEvents.push(...events)
    }

    getLiveEvents() {
        return this.data.liveEvents
    }

    getLiveEventPlayer() {
        return this.liveEventPlayer
    }

    getInsightsApiResults() {
        // will use panel's internal mocks when data is missing in JSON
        return this.data.insightsApiResults || false
    }

    getEnabledFeatures() {
        return this.data.enabledFeatures
    }

    getSharedHeaderConfig() {
        return this.data.sharedHeaderConfig
    }

    getCustomerData() {
        return this.data.customerData
    }

    getOrderData() {
        return this.data.orderData
    }

    getSyncResponse(): CrdtSyncResponse {
        const { delta, serverStateVector, lastTimestamp } = this.data.syncResponse

        return {
            delta: new Uint8Array(delta),
            serverStateVector: new Uint8Array(serverStateVector),
            lastTimestamp,
        }
    }

    downloadMock() {
        const element = document.createElement('a')
        element.setAttribute('href', `data:application/json;charset=utf-8,${encodeURIComponent(JSON.stringify(this.data))}`)
        element.setAttribute('download', 'mock.json')
        element.style.display = 'none'
        document.body.append(element)

        element.click()
        document.body.removeChild(element)
    }

    isAnonymousUser() {
        return this._isAnonymousUser
    }
}

export const initMockManager = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const mock = getMockFileUrl()
    const isAnonymousUser = urlParams.get('anonymous') !== null

    if (isMockActive()) {
        document.mocks = new MockManager(extname(mock) === 'json' ? 'replay' : 'record', mock, isAnonymousUser)
    }
}
initMockManager()

declare global {
    // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
    interface Document {
        mocks: MockManager
    }
}

export {}
